// Transportation Types Configuration
// Similar to TripIt's comprehensive transportation support

export const TRANSPORTATION_TYPES = {
  // Air Travel
  FLIGHT: {
    id: 'flight',
    name: 'Flight',
    icon: '✈️',
    category: 'air',
    color: '#2196F3',
    fields: {
      required: [],
      optional: ['airline', 'flight_number', 'departure_airport', 'arrival_airport', 'departure_time', 'arrival_time', 'confirmation_number', 'seat', 'gate', 'terminal', 'baggage', 'status']
    },
    statusApi: 'duffel', // Use Duffel for flight status
    bookingUrl: 'duffel'
  },

  // Ground Transportation
  TAXI: {
    id: 'taxi',
    name: 'Taxi',
    icon: '🚕',
    category: 'ground',
    color: '#FF9800',
    fields: {
      required: [],
      optional: ['pickup_location', 'dropoff_location', 'pickup_time', 'driver_name', 'vehicle_type', 'estimated_cost', 'actual_cost', 'booking_reference']
    },
    pricingApi: 'uber', // Use Uber for pricing
    bookingUrl: 'uber'
  },

  CAR_RENTAL: {
    id: 'car_rental',
    name: 'Car Rental',
    icon: '🚗',
    category: 'ground',
    color: '#4CAF50',
    fields: {
      required: [],
      optional: ['pickup_location', 'dropoff_location', 'pickup_time', 'return_time', 'rental_company', 'car_type', 'confirmation_number', 'driver_license', 'insurance']
    },
    pricingApi: 'rentalcars', // Use RentalCars.com for pricing
    bookingUrl: 'rentalcars'
  },

  UBER: {
    id: 'uber',
    name: 'Uber',
    icon: '🚖',
    category: 'ground',
    color: '#000000',
    fields: {
      required: [],
      optional: ['pickup_location', 'dropoff_location', 'pickup_time', 'uber_type', 'driver_name', 'estimated_cost', 'actual_cost', 'trip_id']
    },
    pricingApi: 'uber',
    bookingUrl: 'uber'
  },

  LYFT: {
    id: 'lyft',
    name: 'Lyft',
    icon: '🚗',
    category: 'ground',
    color: '#FF00BF',
    fields: {
      required: [],
      optional: ['pickup_location', 'dropoff_location', 'pickup_time', 'lyft_type', 'driver_name', 'estimated_cost', 'actual_cost', 'trip_id']
    },
    pricingApi: 'lyft',
    bookingUrl: 'lyft'
  },

  // Public Transportation
  TRAIN: {
    id: 'train',
    name: 'Train',
    icon: '🚆',
    category: 'public',
    color: '#9C27B0',
    fields: {
      required: [],
      optional: ['departure_station', 'arrival_station', 'departure_time', 'arrival_time', 'train_number', 'car_number', 'seat', 'confirmation_number', 'class']
    },
    statusApi: 'amtrak', // Use Amtrak API for US trains
    bookingUrl: 'amtrak'
  },

  BUS: {
    id: 'bus',
    name: 'Bus',
    icon: '🚌',
    category: 'public',
    color: '#FF5722',
    fields: {
      required: [],
      optional: ['departure_station', 'arrival_station', 'departure_time', 'arrival_time', 'bus_number', 'seat', 'confirmation_number', 'operator']
    },
    statusApi: 'greyhound', // Use Greyhound API for US buses
    bookingUrl: 'greyhound'
  },

  SUBWAY: {
    id: 'subway',
    name: 'Subway',
    icon: '🚇',
    category: 'public',
    color: '#607D8B',
    fields: {
      required: [],
      optional: ['departure_station', 'arrival_station', 'departure_time', 'line', 'direction', 'fare']
    },
    statusApi: 'transit', // Use local transit APIs
    bookingUrl: 'transit'
  },

  // Water Transportation
  FERRY: {
    id: 'ferry',
    name: 'Ferry',
    icon: '⛴️',
    category: 'water',
    color: '#00BCD4',
    fields: {
      required: [],
      optional: ['departure_port', 'arrival_port', 'departure_time', 'arrival_time', 'ferry_company', 'vessel_name', 'confirmation_number', 'vehicle']
    },
    statusApi: 'ferry',
    bookingUrl: 'ferry'
  },

  CRUISE: {
    id: 'cruise',
    name: 'Cruise',
    icon: '🚢',
    category: 'water',
    color: '#3F51B5',
    fields: {
      required: [],
      optional: ['cruise_line', 'ship_name', 'departure_port', 'departure_time', 'cabin_number', 'confirmation_number', 'duration', 'ports_of_call']
    },
    statusApi: 'cruise',
    bookingUrl: 'cruise'
  },

  // Other Transportation
  WALKING: {
    id: 'walking',
    name: 'Walking',
    icon: '🚶',
    category: 'other',
    color: '#795548',
    fields: {
      required: [],
      optional: ['start_location', 'end_location', 'start_time', 'estimated_duration', 'distance', 'route']
    },
    statusApi: null,
    bookingUrl: null
  },

  BICYCLE: {
    id: 'bicycle',
    name: 'Bicycle',
    icon: '🚴',
    category: 'other',
    color: '#8BC34A',
    fields: {
      required: [],
      optional: ['start_location', 'end_location', 'start_time', 'bike_rental', 'estimated_duration', 'distance', 'route']
    },
    statusApi: null,
    bookingUrl: 'bikeshare'
  },

  MOTORCYCLE: {
    id: 'motorcycle',
    name: 'Motorcycle',
    icon: '🏍️',
    category: 'other',
    color: '#FFC107',
    fields: {
      required: [],
      optional: ['start_location', 'end_location', 'start_time', 'rental_company', 'motorcycle_type', 'helmet_required']
    },
    statusApi: null,
    bookingUrl: 'motorcycle_rental'
  }
};

// Transportation categories for grouping
export const TRANSPORTATION_CATEGORIES = {
  AIR: {
    name: 'Air Travel',
    icon: '✈️',
    types: ['FLIGHT']
  },
  GROUND: {
    name: 'Ground Transportation',
    icon: '🚗',
    types: ['TAXI', 'CAR_RENTAL', 'UBER', 'LYFT']
  },
  PUBLIC: {
    name: 'Public Transportation',
    icon: '🚌',
    types: ['TRAIN', 'BUS', 'SUBWAY']
  },
  WATER: {
    name: 'Water Transportation',
    icon: '⛴️',
    types: ['FERRY', 'CRUISE']
  },
  OTHER: {
    name: 'Other',
    icon: '🚶',
    types: ['WALKING', 'BICYCLE', 'MOTORCYCLE']
  }
};

// Helper functions
export const getTransportationType = (typeId) => {
  return TRANSPORTATION_TYPES[typeId.toUpperCase()] || null;
};

export const getTransportationTypesByCategory = (category) => {
  return TRANSPORTATION_CATEGORIES[category.toUpperCase()]?.types || [];
};

export const getAllTransportationTypes = () => {
  return Object.values(TRANSPORTATION_TYPES);
};

export const getTransportationTypesForCSV = () => {
  return Object.values(TRANSPORTATION_TYPES).map(type => ({
    id: type.id,
    name: type.name,
    icon: type.icon,
    category: type.category
  }));
};

// Validation functions
export const validateTransportationItem = (item) => {
  const type = getTransportationType(item.type);
  if (!type) {
    return { valid: false, error: `Invalid transportation type: ${item.type}` };
  }

  // All transportation fields are now optional, so no validation needed
  return { valid: true };
};

// Status check functions
export const getStatusForTransportationItem = async (item) => {
  const type = getTransportationType(item.type);
  if (!type || !type.statusApi) {
    return null;
  }

  try {
    switch (type.statusApi) {
      case 'duffel':
        return await getFlightStatus(item);
      case 'amtrak':
        return await getTrainStatus(item);
      case 'greyhound':
        return await getBusStatus(item);
      case 'transit':
        return await getTransitStatus(item);
      default:
        return null;
    }
  } catch (error) {
    console.warn(`Failed to get status for ${type.name}:`, error);
    return null;
  }
};

// Pricing functions
export const getPricingForTransportationItem = async (item) => {
  const type = getTransportationType(item.type);
  if (!type || !type.pricingApi) {
    return null;
  }

  try {
    switch (type.pricingApi) {
      case 'uber':
        return await getUberPricing(item);
      case 'lyft':
        return await getLyftPricing(item);
      case 'rentalcars':
        return await getCarRentalPricing(item);
      default:
        return null;
    }
  } catch (error) {
    console.warn(`Failed to get pricing for ${type.name}:`, error);
    return null;
  }
};

// API integration functions (to be implemented)
const getFlightStatus = async (item) => {
  // Implement Duffel flight status check
  return null;
};

const getTrainStatus = async (item) => {
  // Implement Amtrak train status check
  return null;
};

const getBusStatus = async (item) => {
  // Implement Greyhound bus status check
  return null;
};

const getTransitStatus = async (item) => {
  // Implement local transit status check
  return null;
};

const getUberPricing = async (item) => {
  // Implement Uber pricing API
  return null;
};

const getLyftPricing = async (item) => {
  // Implement Lyft pricing API
  return null;
};

const getCarRentalPricing = async (item) => {
  // Implement car rental pricing API
  return null;
};
